import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Eye,
  EyeOff,
  Lock,
  LogIn,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { cn } from '@/utils/cn';
import { type LoginFormData, loginSchema } from '@/utils/validators';

export function Login() {
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordMessage, setShowForgotPasswordMessage] = useState(false);
  const successMessage = location.state?.message;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();
    await login(data.username, data.password);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white leading-tight">
          Welcome <span className="text-gradient-brand">Home</span>
        </h2>
        <p className="text-sm font-black text-slate-400 dark:text-white/40 uppercase tracking-widest">
          Access your Neolab workspace
        </p>
      </div>

      {successMessage && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border-2 border-emerald-100 dark:border-emerald-500/20 flex items-start gap-3 backdrop-blur-xl"
        >
          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <p className="text-xs text-emerald-700 dark:text-emerald-300 font-black uppercase tracking-wide">
            {successMessage}
          </p>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="p-4 rounded-xl bg-rose-50 dark:bg-rose-500/10 border-2 border-rose-100 dark:border-rose-500/20 flex items-start gap-3 backdrop-blur-xl"
        >
          <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
          <p className="text-xs text-rose-700 dark:text-rose-300 font-black uppercase tracking-wide">
            {error}
          </p>
        </motion.div>
      )}

      {showForgotPasswordMessage && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="p-4 rounded-xl bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-100 dark:border-amber-500/20 flex items-start gap-3 backdrop-blur-xl"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-amber-700 dark:text-amber-300 font-black uppercase tracking-wide">
              Please contact your administrator to reset your password.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowForgotPasswordMessage(false)}
            className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2.5">
          <label
            htmlFor="username"
            className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em] ml-4"
          >
            Username
          </label>
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-neolab-green transition-colors duration-500">
              <User className="w-5 h-5" />
            </div>
            <input
              id="username"
              type="text"
              autoComplete="username"
              placeholder="e.g. jdoe_neo"
              className={cn(
                'input-field pl-12',
                errors.username && 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/5',
              )}
              {...register('username')}
            />
          </div>
          {errors.username && (
            <p className="text-[10px] font-black text-rose-500 ml-4 uppercase tracking-wider">
              {errors.username.message}
            </p>
          )}
        </div>

        <div className="space-y-2.5">
          <div className="flex justify-between items-center ml-4 mr-4">
            <label
              htmlFor="password"
              className="text-[10px] font-black text-neolab-green/60 dark:text-neolab-green uppercase tracking-[0.4em]"
            >
              Password
            </label>
            <button
              type="button"
              onClick={() => setShowForgotPasswordMessage(true)}
              className="text-[10px] font-black text-slate-400 dark:text-white/30 hover:text-neolab-green transition-colors uppercase tracking-widest"
            >
              Forgotten?
            </button>
          </div>
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-neolab-green transition-colors duration-500">
              <Lock className="w-5 h-5" />
            </div>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={cn(
                'input-field pl-12 pr-12',
                errors.password && 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/5',
              )}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-300 hover:text-neolab-green hover:bg-neolab-green/5 transition-all"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-[10px] font-black text-rose-500 ml-4 uppercase tracking-wider">
              {errors.password.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-4 flex items-center justify-center gap-3 group"
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="text-sm">Verifying Credentials...</span>
            </>
          ) : (
            <>
              <LogIn className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
              <span className="text-sm">Login</span>
              <ArrowRight className="w-4 h-4 opacity-40 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </form>

      <div className="pt-6 text-center">
        <p className="text-slate-400 dark:text-white/30 font-black text-[10px] uppercase tracking-widest">
          No access yet?{' '}
          <Link
            to="/register"
            className="text-neolab-green hover:dark:text-white hover:text-neolab-forest ml-2 font-black transition-all underline underline-offset-4 decoration-2"
          >
            Request Enrollment
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Login;
